import { useState, useEffect, useCallback, useRef } from 'react';
import { ChangeRequestStream } from '@/components/dev/ChangeRequestStream';
import { AboutTechTab } from '@/components/dev/AboutTechTab';
import { DevToolsBar } from '@/components/dev/DevToolsBar';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { ClaudeStatusToast } from '@/components/dev/ClaudeStatusToast';
import { FeatureInventoryTable } from '@/components/dev/FeatureInventoryTable';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import {
  getDevSummary, getDevFeatures, getDevTasks,
  createDevTask, updateDevTask, deleteDevTask,
  createDevFeature, updateDevFeature, deleteDevFeature,
  backupCodeToDrive, ensurePreviewAccounts, getPortalPreviewTokens,
  type DevFeature, type DevTask, type DevSummary,
} from '@lib/api/dev';
import { createBackup, downloadLatestBackup } from '@lib/api/database';
import { DesktopEnvironmentTab } from './DesktopEnvironmentTab';
import { ChangelogTab } from '@/components/dev/ChangelogTab';

// ── Helpers ────────────────────────────────────────────────────────────────

function statusColor(s: DevFeature['status']) {
  const m: Record<string, string> = {
    complete:   'bg-green-100 text-green-800',
    partial:    'bg-yellow-100 text-yellow-800',
    planned:    'bg-blue-100 text-blue-800',
    broken:     'bg-red-100 text-red-800',
    deprecated: 'bg-gray-100 text-gray-500',
  };
  return m[s] ?? 'bg-gray-100 text-gray-600';
}

function taskPriorityColor(p: DevTask['priority']) {
  const m: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    high:     'bg-orange-100 text-orange-800',
    medium:   'bg-yellow-100 text-yellow-800',
    low:      'bg-gray-100 text-gray-600',
  };
  return m[p] ?? '';
}

function taskStatusColor(s: DevTask['status']) {
  const m: Record<string, string> = {
    open:        'bg-blue-50 text-blue-700',
    in_progress: 'bg-purple-100 text-purple-800',
    completed:   'bg-green-100 text-green-800',
    wont_fix:    'bg-gray-100 text-gray-400',
  };
  return m[s] ?? '';
}

export type FeatureNode_ = DevFeature & {
  children: FeatureNode_[];
  _stats: { complete: number; partial: number; planned: number; broken: number; total: number; pct: number };
};

function computeStats(node: FeatureNode_): FeatureNode_['_stats'] {
  if (node.children.length === 0) {
    const pct = node.completion_pct != null ? node.completion_pct :
      node.status === 'complete' ? 100 :
      node.status === 'partial'  ? 50  :
      node.status === 'broken'   ? 10  : 0;
    return {
      complete: node.status === 'complete' ? 1 : 0,
      partial:  node.status === 'partial'  ? 1 : 0,
      planned:  node.status === 'planned'  ? 1 : 0,
      broken:   node.status === 'broken'   ? 1 : 0,
      total: 1,
      pct,
    };
  }
  const child = node.children.reduce(
    (acc, c) => {
      const s = computeStats(c);
      acc.complete += s.complete;
      acc.partial  += s.partial;
      acc.planned  += s.planned;
      acc.broken   += s.broken;
      acc.total    += s.total;
      acc.pct      += s.pct;
      return acc;
    },
    { complete: 0, partial: 0, planned: 0, broken: 0, total: 0, pct: 0 }
  );
  child.pct = child.total > 0 ? Math.round(child.pct / child.total) : 0;
  return child;
}

function buildTree(features: DevFeature[]): FeatureNode_[] {
  const map = new Map<number, FeatureNode_>();
  features.forEach(f => map.set(f.id, { ...f, children: [], _stats: { complete:0,partial:0,planned:0,broken:0,total:1,pct:0 } }));
  const roots: FeatureNode_[] = [];
  features.forEach(f => {
    if (f.parent_id && map.has(f.parent_id)) {
      map.get(f.parent_id)!.children.push(map.get(f.id)!);
    } else if (!f.parent_id) {
      roots.push(map.get(f.id)!);
    }
  });
  roots.forEach(r => { r._stats = computeStats(r); r.children.forEach(c => (c as FeatureNode_)._stats = computeStats(c as FeatureNode_)); });
  return roots;
}

// ── Star Rating Display ────────────────────────────────────────────────────

function StarRating({ value, onChange }: { value: number | null; onChange?: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange?.(i === value ? 0 : i)}
          onMouseEnter={() => onChange && setHover(i)}
          onMouseLeave={() => onChange && setHover(0)}
          className={`text-base leading-none ${onChange ? 'cursor-pointer' : 'cursor-default'}`}
          disabled={!onChange}
        >
          <span className={`material-symbols-outlined text-base ${
            i <= (hover || value || 0)
              ? (value && value >= 4 ? 'text-green-500' : value && value >= 3 ? 'text-amber-400' : 'text-red-400')
              : 'text-gray-200'
          }`} style={{ fontVariationSettings: `'FILL' ${i <= (hover || value || 0) ? 1 : 0}` }}>
            star
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Completion Bar ─────────────────────────────────────────────────────────

function CompletionBar({ stats, pct }: { stats: FeatureNode_['_stats']; pct: number }) {
  const segs = [
    { key: 'complete', color: 'bg-green-500',  v: stats.complete },
    { key: 'partial',  color: 'bg-amber-400',  v: stats.partial  },
    { key: 'planned',  color: 'bg-blue-300',   v: stats.planned  },
    { key: 'broken',   color: 'bg-red-500',    v: stats.broken   },
  ].filter(s => s.v > 0);
  const total = stats.total || 1;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex h-1.5 rounded-full overflow-hidden w-20 bg-gray-100">
        {segs.map(s => (
          <div key={s.key} className={`${s.color} transition-all`} style={{ width: `${(s.v / total) * 100}%` }} />
        ))}
      </div>
      <span className={`text-xs font-medium tabular-nums ${pct === 100 ? 'text-green-600' : pct >= 60 ? 'text-amber-600' : 'text-gray-400'}`}>
        {pct}%
      </span>
    </div>
  );
}

// ── Feature Tree Node ──────────────────────────────────────────────────────

export function FeatureNode({
  node,
  depth,
  onEdit,
  onDelete,
  onAddChild,
  onRate,
  onPreview,
  activePreviewPath,
}: {
  node: FeatureNode_;
  depth: number;
  onEdit: (f: DevFeature) => void;
  onDelete: (id: number) => void;
  onAddChild: (parentId: number) => void;
  onRate: (id: number, rating: number) => void;
  onPreview: (path: string) => void;
  activePreviewPath: string | null;
}) {
  const [open, setOpen] = useState(depth === 0);
  const stats = computeStats(node);
  const hasChildren = node.children.length > 0;
  const isActive = node.route_path ? activePreviewPath === node.route_path : false;

  const childBreakdown = hasChildren
    ? [
        stats.complete > 0 && `${stats.complete} complete`,
        stats.partial  > 0 && `${stats.partial} partial`,
        stats.planned  > 0 && `${stats.planned} planned`,
        stats.broken   > 0 && `${stats.broken} broken`,
      ].filter(Boolean).join(' · ')
    : null;

  return (
    <div className={depth > 0 ? 'ml-5 border-l border-gray-200 pl-3' : ''}>
      <div className={`flex items-start gap-2 py-2 group rounded-lg px-1 transition-colors ${depth === 0 ? 'py-2.5' : ''} ${isActive ? 'bg-primary/5 ring-1 ring-primary/20' : 'hover:bg-gray-50'}`}>
        {/* Expand toggle */}
        <button
          onClick={() => setOpen(o => !o)}
          className={`mt-1 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-transform ${open ? 'rotate-90' : ''} ${!hasChildren ? 'invisible' : ''}`}
        >
          <span className="material-symbols-outlined text-base">chevron_right</span>
        </button>

        {/* Main content */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => node.route_path && onPreview(node.route_path)}>
          {/* Row 1: status · name · route */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusColor(node.status)}`}>
              {node.status}
            </span>
            <span className={`font-medium text-gray-900 text-sm`}>{node.name}</span>
            {node.route_path && (
              <code className={`text-xs px-1 rounded hidden sm:inline ${isActive ? 'bg-primary/10 text-primary font-semibold' : 'bg-gray-100 text-gray-500'}`}>
                {node.route_path}
              </code>
            )}
          </div>

          {/* Row 2: completion bar + quality stars */}
          <div className="flex items-center gap-4 mt-1">
            <CompletionBar stats={stats} pct={stats.pct} />
            <div onClick={e => e.stopPropagation()}>
              <StarRating value={node.quality_rating} onChange={v => onRate(node.id, v)} />
            </div>
            {childBreakdown && (
              <span className="text-xs text-gray-400 hidden md:inline">{childBreakdown}</span>
            )}
          </div>

          {(depth === 0 || open) && node.description && (
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{node.description}</p>
          )}
          {(depth === 0 || open) && node.component_path && (
            <p className="text-xs text-gray-400 font-mono mt-0.5">{node.component_path}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5">
          {node.route_path && (
            <button
              onClick={() => onPreview(node.route_path!)}
              className={`p-1 rounded transition-colors ${isActive ? 'text-primary' : 'text-gray-400 hover:text-primary'}`}
              title="Preview UI"
            >
              <span className="material-symbols-outlined text-sm">visibility</span>
            </button>
          )}
          <button onClick={() => onAddChild(node.id)} className="p-1 text-gray-400 hover:text-primary rounded" title="Add sub-feature">
            <span className="material-symbols-outlined text-sm">add</span>
          </button>
          <button onClick={() => onEdit(node)} className="p-1 text-gray-400 hover:text-primary rounded" title="Edit">
            <span className="material-symbols-outlined text-sm">edit</span>
          </button>
          <button onClick={() => onDelete(node.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Delete">
            <span className="material-symbols-outlined text-sm">delete</span>
          </button>
        </div>
      </div>

      {/* Children */}
      {open && node.children.map(child => (
        <FeatureNode
          key={child.id}
          node={child}
          depth={depth + 1}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddChild={onAddChild}
          onRate={onRate}
          onPreview={onPreview}
          activePreviewPath={activePreviewPath}
        />
      ))}
    </div>
  );
}

// ── UI Gallery (all routes as iframe grid) ────────────────────────────────

export function UIGallery({ features }: { features: DevFeature[] }) {
  const routes = features.filter(f => f.route_path).map(f => ({
    path: f.route_path!,
    name: f.name,
    status: f.status,
    quality: f.quality_rating,
  }));

  if (routes.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-10">No features have route_path set yet.</p>;
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {routes.map(r => (
        <div key={r.path} className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
          {/* iframe container — phone aspect ratio 9:16 */}
          <div className="relative w-full bg-gray-100" style={{ paddingBottom: '177.78%' }}>
            <iframe
              src={r.path}
              title={r.name}
              className="absolute inset-0 w-full h-full border-0"
              style={{ transformOrigin: 'top left', pointerEvents: 'none' }}
            />
          </div>
          <div className="p-2.5 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-800 truncate">{r.name}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColor(r.status)}`}>{r.status}</span>
              <code className="text-xs text-gray-400 truncate">{r.path}</code>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Feature Modal ──────────────────────────────────────────────────────────

function FeatureModal({ feature, parentId, onSave, onClose }: {
  feature?: DevFeature;
  parentId?: number | null;
  onSave: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: feature?.name ?? '',
    description: feature?.description ?? '',
    status: feature?.status ?? 'planned',
    route_path: feature?.route_path ?? '',
    component_path: feature?.component_path ?? '',
    notes: feature?.notes ?? '',
    parent_id: feature?.parent_id ?? parentId ?? null,
    quality_rating: feature?.quality_rating ?? null as number | null,
    completion_pct: feature?.completion_pct ?? null as number | null,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (feature) {
        await updateDevFeature(feature.id, form as Partial<DevFeature>);
      } else {
        await createDevFeature(form as Partial<DevFeature>);
      }
      onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-gray-900">{feature ? 'Edit Feature' : 'Add Feature'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><span className="material-symbols-outlined">close</span></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as DevFeature['status'] }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {['complete','partial','planned','broken','deprecated'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description ?? ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route path</label>
              <input value={form.route_path ?? ''} onChange={e => setForm(f => ({ ...f, route_path: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="/jobs/:id" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Component path</label>
              <input value={form.component_path ?? ''} onChange={e => setForm(f => ({ ...f, component_path: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="src/pages/..." />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quality rating</label>
              <StarRating value={form.quality_rating} onChange={v => setForm(f => ({ ...f, quality_rating: v || null }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Completion % <span className="text-gray-400 font-normal">(override)</span></label>
              <input
                type="number" min={0} max={100}
                value={form.completion_pct ?? ''}
                onChange={e => setForm(f => ({ ...f, completion_pct: e.target.value ? Number(e.target.value) : null }))}
                placeholder="auto"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Task Modal ─────────────────────────────────────────────────────────────

function TaskModal({ task, onSave, onClose }: { task?: DevTask; onSave: () => void; onClose: () => void }) {
  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    status: task?.status ?? 'open',
    priority: task?.priority ?? 'medium',
    category: task?.category ?? 'feature',
    assigned_to: task?.assigned_to ?? 'agent',
    notes: task?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (task) { await updateDevTask(task.id, form as Partial<DevTask>); }
      else       { await createDevTask(form as Partial<DevTask>); }
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-gray-900">{task ? 'Edit Task' : 'Add Task'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><span className="material-symbols-outlined">close</span></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as DevTask['status'] }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {['open','in_progress','completed','wont_fix'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as DevTask['priority'] }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {['critical','high','medium','low'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value as DevTask['category'] }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {['bug','feature','refactor','ux','performance','security','content','infra'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description ?? ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (agent context)</label>
            <textarea value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned to</label>
            <input value={form.assigned_to ?? ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="agent" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

type Tab = 'overview' | 'features' | 'tasks' | 'portal' | 'audit' | 'desktop' | 'changelog' | 'about';
type Viewport = 'phone' | 'tablet' | 'desktop';

export function DevDashboardPage() {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab | null) || 'features';
  const [tab, setTab] = useState<Tab | null>(initialTab); // feature inventory front and center
  const [summary, setSummary] = useState<DevSummary | null>(null);
  const [features, setFeatures] = useState<DevFeature[]>([]);
  const [tasks, setTasks] = useState<DevTask[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [featureModal, setFeatureModal] = useState<{ feature?: DevFeature; parentId?: number | null } | null>(null);
  const [taskModal, setTaskModal] = useState<{ task?: DevTask } | null>(null);

  // Backup state
  const [backing, setBacking] = useState(false);
  const [backupResult, setBackupResult] = useState<string | null>(null);
  const [sqlBacking, setSqlBacking] = useState(false);
  const [sqlBackupResult, setSqlBackupResult] = useState<string | null>(null);

  // Task filter
  const [taskFilter, setTaskFilter] = useState<string>('open');

  // Portal preview state
  const [showOnboardingPreview, setShowOnboardingPreview] = useState(false);
  const [previewSetupBusy, setPreviewSetupBusy] = useState(false);

  const [previewSetupError, setPreviewSetupError] = useState('');
  const [confirmPending, setConfirmPending] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Portal popup overlay state
  const [portalOverlay, setPortalOverlay] = useState<{
    role: 'employee' | 'client';
    path: string;
    username?: string;
    iframeKey: number;
  } | null>(null);
  const [overlayViewport, setOverlayViewport] = useState<'phone' | 'tablet' | 'desktop'>('phone');
  const savedAdminTokens = useRef<{ access: string | null; refresh: string | null } | null>(null);

  const openPortalOverlay = async (role: 'employee' | 'client', path: string) => {
    setPreviewSetupBusy(true);
    setPreviewSetupError('');
    try {
      // Ensure preview accounts exist
      await ensurePreviewAccounts();
      const tokens = await getPortalPreviewTokens();
      const roleData = tokens[role];
      // Stash admin tokens and inject preview tokens into localStorage
      savedAdminTokens.current = {
        access: localStorage.getItem('accessToken'),
        refresh: localStorage.getItem('refreshToken'),
      };
      // Also back up in localStorage so a refresh/navigation during preview can self-heal
      // (see AuthContext restoreSession) instead of stranding the admin on the preview token.
      if (savedAdminTokens.current.access) localStorage.setItem('adminAccessToken', savedAdminTokens.current.access);
      if (savedAdminTokens.current.refresh) localStorage.setItem('adminRefreshToken', savedAdminTokens.current.refresh);
      localStorage.setItem('accessToken', roleData.token);
      localStorage.setItem('refreshToken', roleData.refreshToken);
      setPortalOverlay({ role, path, username: roleData.username, iframeKey: Date.now() });
      setOverlayViewport('phone');
    } catch (err) {
      setPreviewSetupError('Could not load preview. Check server logs.');
      console.error('openPortalOverlay:', err);
    } finally {
      setPreviewSetupBusy(false);
    }
  };

  const closePortalOverlay = () => {
    // Restore admin tokens
    if (savedAdminTokens.current) {
      if (savedAdminTokens.current.access) localStorage.setItem('accessToken', savedAdminTokens.current.access);
      if (savedAdminTokens.current.refresh) localStorage.setItem('refreshToken', savedAdminTokens.current.refresh);
      savedAdminTokens.current = null;
    }
    // Clear the self-heal backups now that the admin session is restored.
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    setPortalOverlay(null);
  };

  // Feature iframe preview state
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [previewViewport, setPreviewViewport] = useState<Viewport>('phone');
  const [galleryMode, setGalleryMode] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  const handlePreview = (path: string) => {
    // Authenticated portal pages can't render under the admin session — the iframe would bounce to
    // the portal login. Route them through the login-skipping overlay (preview token swap) instead.
    if (path.startsWith('/portal/employee')) { openPortalOverlay('employee', path); return; }
    if (path.startsWith('/portal/client'))   { openPortalOverlay('client', path); return; }
    // The portal login/register/callback screens are themselves the preview target — show them with
    // the test credentials banner (see PREVIEW_LOGIN_PATHS render below) rather than skipping login.
    if (previewPath === path) { setPreviewPath(null); return; }
    setPreviewPath(path);
    setGalleryMode(false);
    setIframeKey(k => k + 1);
  };

  const isPreviewLoginScreen = !!previewPath && /^\/portal\/(login|register|callback)/.test(previewPath);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f, t] = await Promise.all([getDevSummary(), getDevFeatures(), getDevTasks()]);
      setSummary(s);
      setFeatures(f);
      setTasks(t);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadTasks = useCallback(async () => {
    const t = await getDevTasks(taskFilter && taskFilter !== 'all' ? { status: taskFilter } : undefined);
    setTasks(t);
  }, [taskFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleBackup = async () => {
    setBacking(true);
    setBackupResult(null);
    try {
      const r = await backupCodeToDrive();
      setBackupResult(`✓ Backed up: ${r.name} (${(r.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (err) {
      setBackupResult(`✗ ${(err as Error).message}`);
    } finally {
      setBacking(false);
    }
  };

  const handleSqlBackup = async () => {
    setSqlBacking(true);
    setSqlBackupResult(null);
    try {
      const r = await createBackup();
      setSqlBackupResult(`✓ ${r.filename} (${(r.sizeBytes / 1024).toFixed(0)} KB)`);
      await downloadLatestBackup();
    } catch (err) {
      setSqlBackupResult(`✗ ${(err as Error).message}`);
    } finally {
      setSqlBacking(false);
    }
  };

  const handleDeleteFeature = (id: number) => {
    setConfirmPending({
      message: 'Delete this feature and all its sub-features?',
      onConfirm: async () => { setConfirmPending(null); await deleteDevFeature(id); loadAll(); },
    });
  };

  const handleDeleteTask = (id: number) => {
    setConfirmPending({
      message: 'Delete this task?',
      onConfirm: async () => { setConfirmPending(null); await deleteDevTask(id); loadTasks(); },
    });
  };

  const featureTree = buildTree(features);

  const handleRateFeature = useCallback(async (id: number, rating: number) => {
    await updateDevFeature(id, { quality_rating: rating || null } as Partial<DevFeature>);
    setFeatures(prev => prev.map(f => f.id === id ? { ...f, quality_rating: rating || null } : f));
  }, []);

  // Tab chips now live in the shared DevToolsBar (#73).

  // Legacy feature-gallery helpers unused since the table became the
  // inventory (#39) — referenced so strict unused checks stay green.
  void previewViewport; void setPreviewViewport; void galleryMode; void iframeKey;
  void handlePreview; void isPreviewLoginScreen; void handleDeleteFeature;
  void featureTree; void handleRateFeature;

  return (
    <MainLayout noPadding aboveBreadcrumbs={
      <DevToolsBar
        active={tab}
        badges={{ audit: tasks.filter(t => t.notes?.includes('audit-') && t.status === 'open').length || undefined }}
        onTab={id => setTab(cur => cur === id ? null : (id as Tab))}
      />
    }>
    <div className="flex flex-col h-full">
      {/* Header — tight: screen space goes to content */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-white flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Dev Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">App map · Change requests · Feature inventory · Backlog</p>
        </div>
        <div className="flex items-center gap-3">
          {sqlBackupResult && (
            <span className={`text-xs px-2 py-1 rounded ${sqlBackupResult.startsWith('✓') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {sqlBackupResult}
            </span>
          )}
          <button
            onClick={handleSqlBackup}
            disabled={sqlBacking}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base">{sqlBacking ? 'sync' : 'download'}</span>
            {sqlBacking ? 'Dumping…' : 'SQL Backup'}
          </button>
          {backupResult && (
            <span className={`text-xs px-2 py-1 rounded ${backupResult.startsWith('✓') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {backupResult}
            </span>
          )}
          <button
            onClick={handleBackup}
            disabled={backing}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base">{backing ? 'sync' : 'cloud_upload'}</span>
            {backing ? 'Backing up…' : 'Backup to Drive'}
          </button>
          <button onClick={loadAll} className="p-2 text-gray-500 hover:text-primary" title="Refresh">
            <span className="material-symbols-outlined text-base">refresh</span>
          </button>
        </div>
      </div>


      <ClaudeStatusToast />

      {/* Dev-tool content — fills the page; Feature Inventory tab holds THE table */}
      <div className={`${tab ? '' : 'hidden'} flex-1 p-3 bg-gray-50 overflow-y-auto`}>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (

          // ── Overview ──────────────────────────────────────────────────
          tab === 'overview' ? (
            <div className="space-y-6 max-w-5xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Features', value: summary?.features.total ?? 0, icon: 'account_tree', color: 'text-blue-600 bg-blue-50' },
                  { label: 'Complete', value: summary?.features.complete ?? 0, icon: 'check_circle', color: 'text-green-600 bg-green-50' },
                  { label: 'In Progress', value: (summary?.features.partial ?? 0), icon: 'pending', color: 'text-amber-600 bg-amber-50' },
                  { label: 'Open Tasks', value: (summary?.tasks.open_count ?? 0) + (summary?.tasks.in_progress ?? 0), icon: 'task_alt', color: 'text-purple-600 bg-purple-50' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className={`inline-flex p-2 rounded-lg ${card.color} mb-3`}>
                      <span className="material-symbols-outlined text-lg">{card.icon}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                    <div className="text-sm text-gray-500">{card.label}</div>
                  </div>
                ))}
              </div>

              {/* Feature health bar */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Feature health</h3>
                <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
                  {[
                    { key: 'complete', color: 'bg-green-500', value: summary?.features.complete ?? 0 },
                    { key: 'partial',  color: 'bg-yellow-400', value: summary?.features.partial ?? 0 },
                    { key: 'planned',  color: 'bg-blue-400', value: summary?.features.planned ?? 0 },
                    { key: 'broken',   color: 'bg-red-500', value: summary?.features.broken ?? 0 },
                  ].map(({ key, color, value }) => {
                    const total = summary?.features.total || 1;
                    return value > 0 ? (
                      <div key={key} className={`${color} transition-all`} style={{ width: `${(value / total) * 100}%` }} title={`${key}: ${value}`} />
                    ) : null;
                  })}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                  {[
                    { label: 'Complete', color: 'bg-green-500', value: summary?.features.complete },
                    { label: 'Partial',  color: 'bg-yellow-400', value: summary?.features.partial },
                    { label: 'Planned',  color: 'bg-blue-400', value: summary?.features.planned },
                    { label: 'Broken',   color: 'bg-red-500', value: summary?.features.broken },
                  ].map(({ label, color, value }) => (
                    <span key={label} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${color}`} />{label}: {value ?? 0}</span>
                  ))}
                </div>
              </div>

              {/* Top open tasks */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Top priority tasks</h3>
                <div className="divide-y divide-gray-100">
                  {tasks.filter(t => t.status === 'open' || t.status === 'in_progress').slice(0, 5).map(t => (
                    <div key={t.id} className="py-2.5 flex items-start gap-3">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${taskPriorityColor(t.priority)}`}>{t.priority}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{t.title}</p>
                        {t.description && <p className="text-xs text-gray-500 truncate">{t.description}</p>}
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${taskStatusColor(t.status)}`}>{t.status.replace('_',' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          // ── Feature Inventory ─────────────────────────────────────────
          // ── Feature Inventory — the table IS the inventory now ─────────
          ) : tab === 'about' ? (
            <AboutTechTab />
          ) : tab === 'features' ? (
            <div className="-m-3">
              <FeatureInventoryTable />
            </div>
          ) : tab === 'tasks' ? (
            <div className="max-w-4xl">
              <ChangeRequestStream />
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Dev backlog</h3>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  {['all','open','in_progress','completed','wont_fix'].map(s => (
                    <button key={s} onClick={() => setTaskFilter(s)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        taskFilter === s ? 'bg-primary text-white border-primary' : 'border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
                      }`}>
                      {s === 'all' ? 'All' : s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                <button onClick={() => setTaskModal({})}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90">
                  <span className="material-symbols-outlined text-base">add</span>Add Task
                </button>
              </div>
              <div className="space-y-2">
                {tasks.map(t => (
                  <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4 group">
                    <div className="flex flex-col gap-1.5 flex-shrink-0 pt-0.5">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${taskPriorityColor(t.priority)}`}>{t.priority}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${taskStatusColor(t.status)}`}>{t.status.replace('_',' ')}</span>
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{t.category}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{t.title}</p>
                      {t.description && <p className="text-sm text-gray-600 mt-1">{t.description}</p>}
                      {t.notes && <p className="text-xs text-gray-400 mt-1.5 italic">{t.notes}</p>}
                      {t.feature_name && <p className="text-xs text-primary mt-1.5">↳ {t.feature_name}</p>}
                      <p className="text-xs text-gray-400 mt-2">Assigned: {t.assigned_to ?? 'unassigned'}</p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => updateDevTask(t.id, { status: t.status === 'open' ? 'in_progress' : t.status === 'in_progress' ? 'completed' : 'open' }).then(loadTasks)}
                        className="p-1.5 text-gray-400 hover:text-green-600" title="Cycle status">
                        <span className="material-symbols-outlined text-sm">update</span>
                      </button>
                      <button onClick={() => setTaskModal({ task: t })} className="p-1.5 text-gray-400 hover:text-primary" title="Edit">
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button onClick={() => handleDeleteTask(t.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Delete">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">No tasks in this filter</div>
                )}
              </div>
            </div>

          // ── Portal Preview ────────────────────────────────────────────
          ) : tab === 'portal' ? (
            <div className="max-w-4xl space-y-6">
              {showOnboardingPreview && (
                <OnboardingWizard onComplete={() => setShowOnboardingPreview(false)} />
              )}

              {previewSetupError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <span className="material-symbols-outlined text-base">error</span>
                  {previewSetupError}
                </div>
              )}

              {/* Employee Portal */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                      <span className="material-symbols-outlined text-lg">badge</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Employee Portal</h3>
                      <p className="text-xs text-gray-400">/portal/employee — mobile-first layout</p>
                    </div>
                  </div>
                  <button
                    disabled={previewSetupBusy}
                    onClick={() => openPortalOverlay('employee', '/portal/employee')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {previewSetupBusy ? <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> : <span className="material-symbols-outlined text-sm">open_in_browser</span>}
                    Preview
                  </button>
                </div>
                <div className="divide-y divide-gray-50">
                  {([
                    { path: '/portal/employee', label: 'Home', icon: 'home', desc: "Today's jobs, quick hour log, schedule preview" },
                    { path: '/portal/employee/schedule', label: 'Schedule', icon: 'calendar_month', desc: 'Full schedule view with job details' },
                    { path: '/portal/employee/timesheet', label: 'Hours / Timesheet', icon: 'schedule', desc: 'Submit, edit, and review time entries' },
                    { path: '/portal/employee/documents', label: 'Documents', icon: 'folder', desc: 'View and download HR/contract documents' },
                    { path: '/portal/employee/notifications', label: 'Notifications', icon: 'notifications', desc: 'Manage notification preferences' },
                  ] as { path: string; label: string; icon: string; desc: string }[]).map(item => (
                    <div key={item.path} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 group">
                      <span className="material-symbols-outlined text-gray-400 text-lg flex-shrink-0">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{item.label}</p>
                        <p className="text-xs text-gray-400 truncate">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => openPortalOverlay('employee', item.path)}
                        disabled={previewSetupBusy}
                        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-blue-600 font-medium transition-opacity disabled:opacity-30"
                      >
                        <span className="material-symbols-outlined text-sm">open_in_browser</span>
                        Preview
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Client Portal */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                      <span className="material-symbols-outlined text-lg">groups</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Client Portal</h3>
                      <p className="text-xs text-gray-400">/portal/client — clean, professional layout</p>
                    </div>
                  </div>
                  <button
                    disabled={previewSetupBusy}
                    onClick={() => openPortalOverlay('client', '/portal/client')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {previewSetupBusy ? <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> : <span className="material-symbols-outlined text-sm">open_in_browser</span>}
                    Preview
                  </button>
                </div>
                <div className="divide-y divide-gray-50">
                  {([
                    { path: '/portal/client', label: 'Home', icon: 'home', desc: 'Active jobs overview, quick-access links' },
                    { path: '/portal/client/schedule', label: 'Schedule', icon: 'calendar_month', desc: 'Upcoming and past job appointments' },
                    { path: '/portal/client/invoices', label: 'Invoices', icon: 'receipt_long', desc: 'View invoices, balance due, payment history' },
                    { path: '/portal/client/documents', label: 'Documents', icon: 'folder', desc: 'Contracts, permits, photos shared by admin' },
                    { path: '/portal/client/notifications', label: 'Notifications', icon: 'notifications', desc: 'Manage notification preferences' },
                  ] as { path: string; label: string; icon: string; desc: string }[]).map(item => (
                    <div key={item.path} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 group">
                      <span className="material-symbols-outlined text-gray-400 text-lg flex-shrink-0">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{item.label}</p>
                        <p className="text-xs text-gray-400 truncate">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => openPortalOverlay('client', item.path)}
                        disabled={previewSetupBusy}
                        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-green-600 font-medium transition-opacity disabled:opacity-30"
                      >
                        <span className="material-symbols-outlined text-sm">open_in_browser</span>
                        Preview
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Utilities */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowOnboardingPreview(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">play_circle</span>
                  Onboarding Wizard
                </button>
                <Link to="/settings?tab=invites" className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className="material-symbols-outlined text-base">mail</span>
                  Manage Invites
                </Link>
              </div>
            </div>

          ) : tab === 'audit' ? (() => {
            const auditItems = tasks.filter(t => t.notes?.includes('audit-'));
            const round1 = auditItems.filter(t => t.notes === 'audit-2026-06-20');
            const round2 = auditItems.filter(t => t.notes === 'audit-2026-06-20b');
            const priorities = ['critical', 'high', 'medium', 'low'] as const;
            const severityColor = {
              critical: 'text-red-600 bg-red-50 border-red-200',
              high: 'text-orange-600 bg-orange-50 border-orange-200',
              medium: 'text-amber-600 bg-amber-50 border-amber-200',
              low: 'text-gray-500 bg-gray-50 border-gray-200',
            };
            const categoryColor: Record<string, string> = {
              security: 'bg-red-100 text-red-700',
              bug: 'bg-orange-100 text-orange-700',
              infra: 'bg-blue-100 text-blue-700',
              performance: 'bg-purple-100 text-purple-700',
              feature: 'bg-gray-100 text-gray-600',
            };
            const totalFixed = auditItems.filter(t => t.status === 'completed' || t.status === 'wont_fix').length;
            const totalOpen = auditItems.filter(t => t.status !== 'completed' && t.status !== 'wont_fix').length;

            const AuditItem = ({ task, p }: { task: DevTask; p: typeof priorities[number] }) => (
              <div className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                task.status === 'completed' || task.status === 'wont_fix'
                  ? task.status === 'wont_fix' ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-green-50 border-green-200'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}>
                <div className="flex-shrink-0 mt-0.5">
                  {task.status === 'completed' ? (
                    <span className="material-symbols-outlined text-green-500 text-xl">check_circle</span>
                  ) : task.status === 'wont_fix' ? (
                    <span className="material-symbols-outlined text-gray-400 text-xl">do_not_disturb_on</span>
                  ) : (
                    <span className={`material-symbols-outlined text-xl ${
                      p === 'critical' ? 'text-red-500' : p === 'high' ? 'text-orange-500' : 'text-amber-500'
                    }`}>error</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className={`font-semibold text-sm ${task.status === 'completed' || task.status === 'wont_fix' ? 'text-gray-500' : 'text-gray-900'}`}>
                      {task.title.replace(/^AUDIT:\s*/, '')}
                    </p>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${categoryColor[task.category] || 'bg-gray-100 text-gray-600'}`}>
                      {task.category}
                    </span>
                    {task.status === 'completed' && <span className="text-xs text-green-600 font-semibold">Fixed</span>}
                    {task.status === 'wont_fix' && <span className="text-xs text-gray-400 font-semibold">Won't fix</span>}
                  </div>
                  {task.description && (
                    <p className="text-xs text-gray-500 leading-relaxed">{task.description}</p>
                  )}
                </div>
                {task.status !== 'wont_fix' && (
                  <button
                    onClick={() => updateDevTask(task.id, {
                      status: task.status === 'completed' ? 'open' : 'completed',
                    }).then(loadTasks)}
                    className="flex-shrink-0 p-1.5 text-gray-300 hover:text-primary rounded transition-colors"
                    title={task.status === 'completed' ? 'Reopen' : 'Mark fixed'}
                  >
                    <span className="material-symbols-outlined text-base">
                      {task.status === 'completed' ? 'undo' : 'check_circle'}
                    </span>
                  </button>
                )}
              </div>
            );

            const AuditRound = ({ items, label, date }: { items: DevTask[]; label: string; date: string }) => {
              const fixed = items.filter(t => t.status === 'completed' || t.status === 'wont_fix').length;
              return (
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</span>
                    <span className="text-xs text-gray-400">{date}</span>
                    <span className="ml-auto text-xs text-gray-400">{fixed}/{items.length} resolved</span>
                    <div className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${Math.round((fixed / Math.max(items.length, 1)) * 100)}%` }} />
                    </div>
                  </div>
                  {priorities.map(p => {
                    const pItems = items.filter(t => t.priority === p);
                    if (!pItems.length) return null;
                    return (
                      <div key={p}>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2 ${
                          p === 'critical' ? 'text-red-600' : p === 'high' ? 'text-orange-600' : p === 'medium' ? 'text-amber-600' : 'text-gray-400'
                        }`}>
                          <span className="material-symbols-outlined text-sm">
                            {p === 'critical' ? 'emergency' : p === 'high' ? 'warning' : p === 'medium' ? 'info' : 'low_priority'}
                          </span>
                          {p}
                        </h3>
                        <div className="space-y-2">
                          {pItems.map(task => <AuditItem key={task.id} task={task} p={p} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            };

            return (
              <div className="max-w-3xl space-y-6">
                {/* Header card */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-11 w-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-2xl">security</span>
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-900 text-lg">Security & Code Audit</h2>
                      <p className="text-sm text-gray-500">2 rounds · Jun 20 2026 · {auditItems.length} findings · {totalFixed} resolved · {totalOpen} open</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-bold text-gray-900">{Math.round((totalFixed / Math.max(auditItems.length, 1)) * 100)}%</div>
                      <div className="text-xs text-gray-400">resolved</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {priorities.map(p => {
                      const items = auditItems.filter(t => t.priority === p);
                      const fixed = items.filter(t => t.status === 'completed' || t.status === 'wont_fix').length;
                      return (
                        <div key={p} className={`rounded-lg border p-3 text-center ${severityColor[p]}`}>
                          <p className="text-xs uppercase tracking-wide font-semibold opacity-70">{p}</p>
                          <p className="text-xl font-bold mt-1">{items.length}</p>
                          <p className="text-xs opacity-60">{fixed}/{items.length} fixed</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {round1.length > 0 && <AuditRound items={round1} label="Round 1 — Initial audit" date="Jun 20 2026" />}
                {round2.length > 0 && (
                  <>
                    <div className="border-t border-dashed border-gray-200" />
                    <AuditRound items={round2} label="Round 2 — Follow-up audit" date="Jun 20 2026" />
                  </>
                )}
              </div>
            );
          })() : tab === 'desktop' ? (
            <DesktopEnvironmentTab />
          ) : tab === 'changelog' ? (
            <ChangelogTab />
          ) : null
        )}
      </div>

      {/* Modals */}
      {featureModal !== null && (
        <FeatureModal
          feature={featureModal.feature}
          parentId={featureModal.parentId}
          onSave={() => { setFeatureModal(null); loadAll(); }}
          onClose={() => setFeatureModal(null)}
        />
      )}
      {taskModal !== null && (
        <TaskModal
          task={taskModal.task}
          onSave={() => { setTaskModal(null); loadTasks(); }}
          onClose={() => setTaskModal(null)}
        />
      )}
      {confirmPending && (
        <ConfirmDialog
          message={confirmPending.message}
          onConfirm={confirmPending.onConfirm}
          onCancel={() => setConfirmPending(null)}
        />
      )}

      {/* ── Portal Popup Overlay ──────────────────────────────────────── */}
      {portalOverlay && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 h-12 bg-gray-950 border-b border-gray-800 flex-shrink-0">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
              portalOverlay.role === 'employee' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
            }`}>
              <span className="material-symbols-outlined text-sm">{portalOverlay.role === 'employee' ? 'badge' : 'groups'}</span>
              {portalOverlay.role === 'employee' ? 'Employee' : 'Client'} Portal
            </div>
            {portalOverlay.username && (
              <span className="text-xs text-gray-400">
                as <span className="text-gray-200 font-medium">{portalOverlay.username}</span>
              </span>
            )}
            <span className="text-xs text-gray-600 font-mono hidden sm:inline">{portalOverlay.path}</span>

            {/* Viewport toggles */}
            <div className="ml-auto flex items-center gap-1">
              {(['phone', 'tablet', 'desktop'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setOverlayViewport(v)}
                  className={`p-1.5 rounded transition-colors ${overlayViewport === v ? 'text-white bg-gray-700' : 'text-gray-500 hover:text-gray-300'}`}
                  title={v}
                >
                  <span className="material-symbols-outlined text-base">
                    {v === 'phone' ? 'smartphone' : v === 'tablet' ? 'tablet' : 'computer'}
                  </span>
                </button>
              ))}
              <a
                href={portalOverlay.path}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 p-1.5 text-gray-500 hover:text-gray-300 rounded"
                title="Open in new tab"
              >
                <span className="material-symbols-outlined text-base">open_in_new</span>
              </a>
              <button
                onClick={closePortalOverlay}
                className="ml-1 p-1.5 text-gray-500 hover:text-white rounded transition-colors"
                title="Close"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          </div>

          {/* Preview area */}
          <div className="flex-1 flex items-start justify-center overflow-auto bg-gray-900 p-4">
            <div
              className="bg-white shadow-2xl overflow-hidden flex-shrink-0 transition-all duration-200"
              style={{
                width: overlayViewport === 'phone' ? 390 : overlayViewport === 'tablet' ? 768 : '100%',
                maxWidth: overlayViewport === 'desktop' ? '100%' : undefined,
                height: overlayViewport === 'phone' ? 844 : overlayViewport === 'tablet' ? 1024 : 'calc(100vh - 96px)',
                borderRadius: overlayViewport === 'desktop' ? 0 : 12,
              }}
            >
              <iframe
                key={portalOverlay.iframeKey}
                src={portalOverlay.path}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                title="Portal Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
